"use client";

import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { extractYouTubeVideoId } from "@/utils/video";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as z from "@/lib/propSchema";
import Image from "next/image";
import { LeftArrow, RightArrow } from "@/assets/Arrow";
import { ProgressSlider } from "@/components/atoms/ProgressSlider";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import StackCarousel, {
    type Card as StackCarouselCard,
} from "@/components/organisms/carousels/StackCarousel";

function isYouTubeVideoUrl(url?: string) {
    return Boolean(url?.trim() && extractYouTubeVideoId(url.trim()));
}

/** Section header + card copy caps (truncate when exceeded). */
const PROGRAM_BENEFITS2_COPY_LIMITS = {
    preTitle: 20,
    title: 20,
    subText: 250,
    cardTitle: 45,
    cardSubTitle: 190,
} as const;

function clampStr(s: string, max: number) {
    return s.length <= max ? s : s.slice(0, max);
}

/**
 * API/Redux often sends `""` for optional colors. Zod's `.default()` only runs for `undefined`,
 * so empty strings must be coalesced or gradients never apply in `formatBackgroundStyle`
 * (variant_1/2 need a truthy `gradient_color_1`). Same idea as normalizing `program_benefits` row data.
 */
function coalesceEmptyToUndefined<T>(v: T): T | undefined {
    if (v == null) return undefined;
    if (typeof v === "string" && v.trim() === "") return undefined;
    return v;
}

/**
 * A string field that is allowed to be unset.
 *
 * The CMS spells "not set" three different ways for the same field: the key is ABSENT when
 * the row predates the field, `""` when an author cleared it, and `null` when the column was
 * added by a migration and never written. Zod's `.default()` only fires on `undefined`, so
 * the other two fall through to the string check and fail — and a failed parse here returns
 * `null` from the whole component, so ONE never-written column makes the entire section
 * disappear from the page. Every optional string goes through this; none of them is worth a
 * blank page.
 */
function optionalText(fallback = "") {
    return z.preprocess(coalesceEmptyToUndefined, z.string().optional()).default(fallback);
}

/** `optionalText` for a field with a copy cap. */
function cappedText(max: number, fallback = "") {
    return optionalText(fallback).transform((s) => clampStr(s, max));
}

/**
 * A boolean field that is allowed to be unset.
 *
 * MySQL hands tinyint(1) back as 0/1 (and as "0"/"1" through some driver paths), never as a
 * JS boolean, so a bare `z.boolean()` fails on the value the API actually sends. Unset — of
 * any of the three spellings above — falls back rather than reading as false, which matters
 * for the flags that default to TRUE.
 */
function optionalFlag(fallback: boolean) {
    return z.preprocess(
        (v) => (coalesceEmptyToUndefined(v) === undefined ? fallback : v === true || v === 1 || v === "1"),
        z.boolean(),
    );
}

/** CMS may send "90deg", "90", or "to right" — linear-gradient() needs a valid angle/side (same as Testimonials2). */
function normalizeTitleGradientAngle(raw: unknown): string {
    const t = String(raw ?? "").trim();
    if (!t) return "90deg";
    if (/^to\s/i.test(t)) return t;
    if (/deg|grad|rad|turn$/i.test(t)) return t;
    if (/^-?\d+(\.\d+)?$/.test(t)) return `${t}deg`;
    return t;
}

/** Centered play control — only when a valid YouTube URL is configured (same as Testimonials2). */
function BenefitVideoPlayButton({
    onPlay,
    className,
}: {
    onPlay: () => void;
    className?: string;
}) {
    return (
        <div
            className={cn(
                "pointer-events-none absolute inset-0 z-20 flex items-center justify-center",
                className
            )}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPlay();
                }}
                className={cn(
                    "pointer-events-auto flex size-12 cursor-pointer items-center justify-center rounded-full",
                    "bg-white/92 text-[#0a0a0a] shadow-lg ring-1 ring-black/10 transition hover:scale-105 hover:bg-white",
                    "min-[1080px]:size-14 min-[1230px]:size-16"
                )}
                aria-label="Play benefit video"
            >
                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="ml-0.5"
                    aria-hidden
                >
                    <path d="M8 5v14l11-7z" />
                </svg>
            </button>
        </div>
    );
}

// Card schema aligned with programBenefits_2.ts METADATA_FIELDS + DEFAULT_ROW
const ProgramBenefits2CardSchema = z.object({
    id: z.union([z.number(), z.string()]),
    // Empty is a rendered state, not a broken one — BenefitCard draws its dark fallback
    // block when there is no image, so a card with an unset image costs one card, not the
    // whole section.
    meta_image: optionalText(""),
    meta_title: cappedText(PROGRAM_BENEFITS2_COPY_LIMITS.cardTitle),
    meta_sub_title: cappedText(PROGRAM_BENEFITS2_COPY_LIMITS.cardSubTitle),
    meta_alt_image: optionalText(""),
    meta_video_link: optionalText(""),
    meta_title_color: optionalText("#FFFFFF"),
    meta_sub_title_color: optionalText("#FFFFFF"),
    meta_priority: z
        .preprocess(coalesceEmptyToUndefined, z.union([z.coerce.number(), z.string()]).optional())
        .default(0),
    meta_card_blur_effect: optionalFlag(false),
});

type ProgramBenefits2Card = z.infer<typeof ProgramBenefits2CardSchema>;
type ProgramBenefits2StackCard = StackCarouselCard & {
    rawCard: ProgramBenefits2Card;
    videoUrl: string;
};

// Card per Figma: mobile 335×200 r16 (node 3583:10767); sm+ 520×420 / 620×500 r40
const BenefitCard: FC<{
    card: ProgramBenefits2Card;
    onOpenVideo?: (url: string) => void;
}> = ({ card, onOpenVideo }) => {
    const videoUrl = card.meta_video_link?.trim();
    const showVideoPlay = isYouTubeVideoUrl(videoUrl);
    const handleMediaClick = () => {
        if (videoUrl && extractYouTubeVideoId(videoUrl)) onOpenVideo?.(videoUrl);
    };

    return (
        <div
            className={cn(
                "relative overflow-clip shrink-0 flex flex-col justify-end rounded-[16px]",
                // Mobile (Figma exact): 335×200, 16px radius
                "w-[335px] h-[200px] min-[1080px]:rounded-[40px]",
                // 1080–1429: 520×420, rounded 40px
                "min-[1080px]:w-[520px] min-[1080px]:h-[420px]",
                // 1430+: 620×500
                "min-[1430px]:w-[620px] min-[1430px]:h-[500px] min-[1750px]:w-[620px] min-[1750px]:h-[500px]"
            )}
        >
            {card.meta_image?.trim() ? (
                <>
                    <Image
                        src={card.meta_image}
                        alt={card.meta_alt_image ?? card.meta_title}
                        fill
                        className="object-cover"
                    />
                    {showVideoPlay ? (
                        <>
                            <button
                                type="button"
                                className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
                                onClick={handleMediaClick}
                                aria-label="Play benefit video"
                            />
                            <BenefitVideoPlayButton onPlay={handleMediaClick} />
                        </>
                    ) : null}
                </>
            ) : (
                <div className="absolute inset-0 bg-[#1a1a1a]">
                    {showVideoPlay ? (
                        <>
                            <button
                                type="button"
                                className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
                                onClick={handleMediaClick}
                                aria-label="Play benefit video"
                            />
                            <BenefitVideoPlayButton onPlay={handleMediaClick} />
                        </>
                    ) : null}
                </div>
            )}
            {card.meta_card_blur_effect && (
                <div
                    className={cn(
                        "absolute inset-x-0 bottom-0 pointer-events-none",
                        "h-[82px] min-[1080px]:left-1/2 min-[1080px]:-translate-x-1/2 min-[1080px]:h-[227px] min-[1080px]:w-[520px] min-[1430px]:w-[620px] min-[1750px]:w-[620px]",
                        "backdrop-blur-[5px]",
                        "backdrop-saturate-[70%]",
                        "backdrop-brightness-50",
                        "[mask-image:linear-gradient(to_top,black_0%,black_65%,rgba(0,0,0,0.2)_90%,transparent_97%)]"
                    )}
                />
            )}
            <div
                className={cn(
                    "absolute left-1/2 -translate-x-1/2 flex flex-col z-10 text-white",
                    // xs: top 140px, w 315px, gap 4px
                    "top-[140px] w-[315px] gap-[4px]",
                    // 1080–1429: top 317px, w 456px, gap 8px
                    "min-[1080px]:top-[317px] min-[1080px]:w-[456px] min-[1080px]:gap-[8px]",
                    // 1430+: top 376px, w 541px, gap 8px
                    "min-[1430px]:top-[376px] min-[1430px]:w-[541px] min-[1430px]:gap-[8px]",
                    "min-[1750px]:top-[376px] min-[1750px]:w-[541px] min-[1750px]:gap-[8px]"
                )}
            >
                <p
                    className={cn(
                        "font-semibold leading-[1.4] w-full line-clamp-2",
                        "text-[14px] tracking-[0.14px]",
                        "min-[1080px]:text-[20px] min-[1080px]:tracking-[0.2px]",
                        "min-[1430px]:text-[24px] min-[1430px]:tracking-[0.24px]",
                        "min-[1750px]:text-[24px] min-[1750px]:tracking-[0.24px]"
                    )}
                    style={{
                        fontFamily: "var(--font-google-sans)",
                        color: card.meta_title_color,
                    }}
                >
                    {card.meta_title}
                </p>
                <p
                    className={cn(
                        "font-medium leading-[1.49] w-full line-clamp-3 min-[1080px]:line-clamp-3",
                        "text-[9px]",
                        "min-[1080px]:text-[12px]",
                        "min-[1430px]:text-[14px] min-[1750px]:text-[14px]"
                    )}
                    style={{
                        fontFamily: "var(--font-google-sans)",
                        color: card.meta_sub_title_color,
                    }}
                >
                    {card.meta_sub_title}
                </p>
            </div>
        </div>
    );
};

// Section schema aligned with programBenefits_2.ts FIELDS_CONFIG
const ProgramBenefits2DataSchema = z.object({
    desktop_background_color: optionalText("#FFFFFF"),
    mobile_background_color: optionalText("#FFFFFF"),
    backvariant: z
        .preprocess(coalesceEmptyToUndefined, z.enum(["variant_1", "variant_2", "variant_3"]).optional())
        .default("variant_1"),
    gradient_color_1: optionalText("rgba(0, 212, 235, 0.33)"),
    gradient_color_2: optionalText("rgba(0, 0, 0, 0.66)"),
    gradient_color_3: optionalText("rgba(0, 0, 0, 0.66)"),
    gradient_color_4: optionalText("rgba(83, 69, 195, 0.46)"),
    pre_title: cappedText(PROGRAM_BENEFITS2_COPY_LIMITS.preTitle),
    pre_title_color: optionalText("#222222CC"),
    title: cappedText(PROGRAM_BENEFITS2_COPY_LIMITS.title),
    title_start_color: optionalText("#FFFFFF"),
    title_end_color: optionalText("#00D4EB"),
    /** Same as ProgramBenefits1: bare numbers get `deg` so `linear-gradient()` always receives valid CSS. */
    title_gradient_direction: z
        .preprocess(
            (v) => {
                if (v == null || (typeof v === "string" && v.trim() === "")) return "90deg";
                return String(v);
            },
            z.string()
        )
        .transform((s) => normalizeTitleGradientAngle(s)),
    description: cappedText(PROGRAM_BENEFITS2_COPY_LIMITS.subText),
    description_color: optionalText("#FFFFFFAF"),
    left_navigation_icon: optionalText(""),
    alt_text_left_nav_icon: optionalText(""),
    right_navigation_icon: optionalText(""),
    alt_text_right_nav_icon: optionalText(""),
    scroll_frequency: z
        .preprocess(coalesceEmptyToUndefined, z.coerce.number().optional())
        .default(0),
    metadata: z
        .preprocess(coalesceEmptyToUndefined, z.array(ProgramBenefits2CardSchema).optional())
        .default([]),
    show_carousel_arrows: optionalFlag(true),
    /** MySQL / API often send 0/1; align with ParaText1 truthy check. */
    enable_animation: optionalFlag(false),
    /** Toggles desktop carousel implementation: stacked deck (true) vs original horizontal scroll (false). */
    enable_stack_carousel: optionalFlag(false),
}).transform((data) => ({
    ...data,
    metadata: data.metadata?.sort(
        (a: ProgramBenefits2Card, b: ProgramBenefits2Card) =>
            (Number(a.meta_priority) || 0) - (Number(b.meta_priority) || 0)
    ),
}));

export const ProgramBenefits2: FC<any> = (props) => {
    const actualData = props?.data?.data || props?.data || props;

    const parsedResult = useMemo(() => ProgramBenefits2DataSchema.safeParse(actualData), [actualData]);
    if (!parsedResult.success) {
        console.error("ProgramBenefits2 validation failed", parsedResult.error);
        return null;
    }

    const sectionRef = useRef<HTMLElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cardRefsRef = useRef<(HTMLDivElement | null)[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [videoOpen, setVideoOpen] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState("");

    const parsedData = parsedResult.data;
    const isMobile = useIsMobile();
    const cards = parsedData.metadata;
    const cardCount = cards.length;
    const useStackCarousel = parsedData.enable_stack_carousel;

    const autoScrollMs = useMemo(() => {
        const raw = parsedData.scroll_frequency;
        if (raw === undefined || raw === null) return 0;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }, [parsedData.scroll_frequency]);

    const scrollToIndexRequestedRef = useRef(false);
    const scrollIgnoreUntilRef = useRef(0);

    /** Scroll the carousel strip only — never `scrollIntoView` (that scrolls the page). */
    const scrollCarouselToCardIndex = useCallback(
        (container: HTMLDivElement, cardEl: HTMLElement, mode: "start" | "end") => {
            const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
            const cr = container.getBoundingClientRect();
            const er = cardEl.getBoundingClientRect();
            if (mode === "start") {
                const nextLeft = container.scrollLeft + (er.left - cr.left);
                container.scrollTo({
                    left: Math.min(Math.max(0, nextLeft), maxScrollLeft),
                    behavior: "smooth",
                });
            } else {
                const nextLeft = container.scrollLeft + (er.right - cr.right);
                container.scrollTo({
                    left: Math.min(Math.max(0, nextLeft), maxScrollLeft),
                    behavior: "smooth",
                });
            }
        },
        []
    );

    const onScroll = useCallback(() => {
        if (scrollToIndexRequestedRef.current) return;
        if (Date.now() < scrollIgnoreUntilRef.current) return;
        const container = scrollContainerRef.current;
        if (!container || cardCount <= 1) return;
        const scrollLeft = container.scrollLeft;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const gap = 21;
        const w = typeof window !== "undefined" ? window.innerWidth : 1080;
        const cardW = w >= 1430 ? 620 : w >= 1080 ? 520 : 520;
        const step = cardW + gap;
        const threshold = step * 0.5;
        const atEnd = maxScrollLeft <= 0 || scrollLeft >= maxScrollLeft - threshold;
        const index = atEnd
            ? cardCount - 1
            : Math.min(Math.round(scrollLeft / step), cardCount - 1);
        setCurrentIndex((prev) => {
            const next = Math.min(Math.max(0, index), cardCount - 1);
            return next !== prev ? next : prev;
        });
    }, [cardCount]);

    const handlePrevClick = useCallback(() => {
        setCurrentIndex((prev) =>
            cardCount === 3 && prev === cardCount - 1 ? 0 : Math.max(0, prev - 1)
        );
        scrollToIndexRequestedRef.current = true;
    }, [cardCount]);
    const handleNextClick = useCallback(() => {
        setCurrentIndex((prev) =>
            cardCount === 3 && prev === 0 ? cardCount - 1 : Math.min(cardCount - 1, prev + 1)
        );
        scrollToIndexRequestedRef.current = true;
    }, [cardCount]);

    const openVideo = useCallback((url: string) => {
        setActiveVideoUrl(url);
        setVideoOpen(true);
    }, []);

    useEffect(() => {
        if (useStackCarousel) return;
        if (!scrollToIndexRequestedRef.current || isMobile) return;
        const container = scrollContainerRef.current;
        const el = cardRefsRef.current[currentIndex];
        if (container && el) {
            if (currentIndex === 0) {
                container.scrollTo({ left: 0, behavior: "smooth" });
            } else if (currentIndex === cardCount - 1) {
                scrollCarouselToCardIndex(container, el, "end");
            } else {
                scrollCarouselToCardIndex(container, el, "start");
            }
            scrollIgnoreUntilRef.current = Date.now() + 600;
        }
        const t = setTimeout(() => {
            scrollIgnoreUntilRef.current = 0;
            scrollToIndexRequestedRef.current = false;
        }, 650);
        return () => clearTimeout(t);
    }, [currentIndex, cardCount, isMobile, scrollCarouselToCardIndex, useStackCarousel]);

    useEffect(() => {
        if (useStackCarousel) return;
        if (isMobile || cardCount <= 1 || autoScrollMs <= 0 || videoOpen) return;
        const id = setInterval(() => {
            scrollToIndexRequestedRef.current = true;
            setCurrentIndex((prev) => {
                if (prev >= cardCount - 1) return 0;
                return cardCount === 3 && prev === 0 ? cardCount - 1 : prev + 1;
            });
        }, autoScrollMs);
        return () => clearInterval(id);
    }, [isMobile, cardCount, autoScrollMs, videoOpen, useStackCarousel]);

    const stackCards = useMemo<ProgramBenefits2StackCard[]>(
        () =>
            cards.map((card) => ({
                image: card.meta_image,
                imageAlt: card.meta_alt_image || card.meta_title,
                title: card.meta_title,
                description: card.meta_sub_title,
                titleColor: card.meta_title_color,
                descriptionColor: card.meta_sub_title_color,
                blurBottom: card.meta_card_blur_effect,
                rawCard: card,
                videoUrl: card.meta_video_link?.trim() || "",
            })),
        [cards]
    );

    const handleStackCardClick = useCallback(
        (card: ProgramBenefits2StackCard) => {
            if (isYouTubeVideoUrl(card.videoUrl)) {
                openVideo(card.videoUrl);
            }
        },
        [openVideo]
    );

    // Same mapping as ProgramBenefits1: pass the row-shaped object; `formatBackgroundStyle` only reads backvariant + colors.
    const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");
    const isAnimationEnabled = parsedData.enable_animation;

    return (
      <>
        <section
          ref={sectionRef}
          className={cn(
            // The padding here and the column width below are a matched pair: each tier
            // sums to exactly its own breakpoint (60+960+60=1080, 80+1070+80=1230,
            // 80+1280+80=1440, 140+1470+140=1750). Break the pairing and the fixed-width
            // column below overflows, `items-center` splits the overflow evenly, and the
            // side padding visually collapses — `overflow-x-hidden` swallows the right
            // side, so it reads as "the left gutter disappeared". These are this repo's
            // redefined breakpoints (sm/md/lg/xl = 1080/1230/1430/1750, NOT Tailwind's
            // 640/768/1024/1280); spelled out because translating them to the default
            // scale once shipped exactly that bug.
            "relative w-full overflow-x-hidden flex flex-col items-center",
            "xs:px-[24px] xs:py-[40px]",
            "min-[1080px]:px-[60px] min-[1080px]:py-[40px]",
            "min-[1230px]:px-[80px] min-[1230px]:py-[60px]",
            "min-[1430px]:px-[80px] min-[1430px]:py-[60px]",
            "min-[1750px]:px-[140px] min-[1750px]:py-[80px]",
          )}
          style={bgColor}
        >
          <div
            className={cn(
              "relative z-10 w-full",
              "max-w-[335px] mx-auto min-[1080px]:mx-0 min-[1080px]:max-w-none",
              "w-full min-[1080px]:w-[960px]",
              "min-[1230px]:w-[1070px]",
              "min-[1430px]:w-[1280px]",
              "min-[1750px]:w-[1470px]",
            )}
          >
            <div
              className={cn(
                "relative flex flex-col mb-[24px]",
                "min-[1080px]:mb-[27px]",
                "min-[1430px]:mb-[36px]",
              )}
            >
              {/* Both lines are one <h2>. Spans throughout, because a heading may only
                  hold phrasing content; `block` restores the line break the <p> gave. */}
              <h2 className="flex flex-col">
              <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
                <span
                  className={cn(
                    "block font-mediumn leading-normal whitespace-nowrap",
                    "text-[24px]",
                    "min-[1080px]:text-[22px]",
                    "min-[1230px]:text-[26px]",
                    "min-[1430px]:text-[30px] min-[1750px]:text-[30px]",
                    "mb-[2px]",
                  )}
                  style={{
                    color: parsedData.pre_title_color,
                    fontFamily: "var(--font-google-sans)",
                  }}
                >
                  {parsedData.pre_title}
                </span>
              </RevealOnScroll>

              <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
                <span
                  className={cn(
                    "font-bold leading-[1.2] bg-clip-text gradient-title-denton mb-6",
                    // Mobile: allow wrapping & clamp to 2 lines
                    "whitespace-normal line-clamp-2",
                    // Tablet and up: single line, no wrapping, no clamp
                    "min-[1080px]:whitespace-nowrap min-[1080px]:line-clamp-none",
                    "text-[40px]",
                    "min-[1080px]:text-[46px]",
                    "min-[1230px]:text-[56px]",
                    "min-[1430px]:text-[64px] min-[1750px]:text-[64px]",
                  )}
                  style={{
                    // Match ProgramBenefits1: `backgroundImage` + angle from Zod (already normalized) so CMS angle updates apply.
                    backgroundImage:
                      parsedData.title_start_color && parsedData.title_end_color
                        ? `linear-gradient(${parsedData.title_gradient_direction}, ${parsedData.title_start_color}, ${parsedData.title_end_color})`
                        : undefined,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: "var(--font-merriweather)",
                  }}
                >
                  {parsedData.title}
                </span>
              </RevealOnScroll>
              </h2>
              {parsedData.description ? (
                <RevealOnScroll enableAnimation={isAnimationEnabled}>
                  <p
                    className={cn(
                      "w-full min-w-0 wrap-break-word",
                      "leading-[1.49] mb-[40px]",
                      "text-[12px]",
                      "min-[1080px]:text-[14px]",
                      "min-[1230px]:text-[16px]",
                      "min-[1430px]:text-[18px] min-[1750px]:text-[18px] mb-1",
                    )}
                    style={{
                      color:
                        parsedData.description_color || "rgba(255,255,255,0.7)",
                      fontFamily: "var(--font-google-sans)",
                    }}
                  >
                    {parsedData.description}
                  </p>
                </RevealOnScroll>
              ) : null}
            </div>

            {/* Cards: mobile = vertical stack, desktop = full-width horizontal scroll */}
            {cards.length > 0 && (
              <>
                {/* Legacy mode only: mobile fallback remains vertical cards. */}
                {!useStackCarousel && (
                  <div
                    className={cn("flex flex-col gap-[24px] w-full min-[1080px]:hidden")}
                  >
                    {cards.map((card) => (
                      <RevealOnScroll
                        key={String(card.id)}
                        enableAnimation={isAnimationEnabled}
                      >
                        <BenefitCard card={card} onOpenVideo={openVideo} />
                      </RevealOnScroll>
                    ))}
                  </div>
                )}
                {/*
                  Desktop carousel: switched by `enable_stack_carousel`.
                  - true  -> new StackCarousel (left-stacking deck)
                  - false -> original horizontal-scroll carousel + ProgressSlider nav
                */}
                {useStackCarousel ? (
                  <RevealOnScroll enableAnimation={isAnimationEnabled}>
                    <div
                      className={cn(
                        "w-screen relative left-1/2 -translate-x-1/2",
                        "mb-[40px]",
                      )}
                    >
                      <StackCarousel
                        cards={stackCards}
                        leftInsetPct={0}
                        className={cn(
                          "w-full",
                          // Track the centered header column's left edge at every
                          // width (not just the Figma design widths) so the first
                          // card lines up with the heading. X = header column
                          // width / 2 per breakpoint (960/1070/1280/1470).
                          "min-[1080px]:pl-[calc(50%-480px)]",
                          "min-[1230px]:pl-[calc(50%-535px)]",
                          "min-[1430px]:pl-[calc(50%-640px)]",
                          "min-[1750px]:pl-[calc(50%-735px)]",
                        )}
                        viewportClassName={cn(
                          // Match legacy BenefitCard heights so internal text
                          // positions (top-[317px]/[376px]) sit pixel-perfect.
                          "h-[200px] min-[1080px]:h-[420px] min-[1430px]:h-[500px]",
                          "px-[24px] min-[1080px]:px-[60px] min-[1230px]:px-[80px] min-[1430px]:px-[80px] min-[1750px]:px-[140px]",
                        )}
                        contentClassName={cn(
                          "rounded-[16px] min-[1080px]:rounded-[40px]",
                          "transform-gpu will-change-transform [backface-visibility:hidden]",
                          "transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                          "hover:scale-[1.02] hover:-rotate-[1.6deg]",
                        )}
                        showNavigation={parsedData.show_carousel_arrows}
                        autoScrollMs={autoScrollMs}
                        pauseAutoScroll={videoOpen}
                        onCardClick={handleStackCardClick}
                        renderOverlay={(card) =>
                          isYouTubeVideoUrl(card.videoUrl) ? (
                            <>
                              <button
                                type="button"
                                className="absolute inset-0 z-5 cursor-pointer border-0 bg-transparent p-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openVideo(card.videoUrl);
                                }}
                                aria-label="Play benefit video"
                              />
                              <BenefitVideoPlayButton
                                onPlay={() => openVideo(card.videoUrl)}
                              />
                            </>
                          ) : null
                        }
                      />
                    </div>
                  </RevealOnScroll>
                ) : (
                  <>
                    <RevealOnScroll enableAnimation={isAnimationEnabled}>
                      <div
                        className={cn(
                          "hidden min-[1080px]:block w-screen relative left-1/2 -translate-x-1/2",
                          "mb-[40px]",
                        )}
                      >
                        <div
                          ref={scrollContainerRef}
                          onScroll={onScroll}
                          className={cn(
                            "flex gap-[21px] overflow-x-auto overflow-y-clip scrollbar-hide",
                            // Match the centered header column's left edge at every
                            // width so the first card lines up with the heading.
                            "min-[1080px]:pl-[calc(50%-480px)] min-[1230px]:pl-[calc(50%-535px)] min-[1430px]:pl-[calc(50%-640px)] min-[1750px]:pl-[calc(50%-735px)]",
                            "pr-[60px] min-[1230px]:pr-[80px] min-[1430px]:pr-[80px] min-[1750px]:pr-[140px]",
                          )}
                        >
                          {cards.map((card, i) => (
                            <div
                              key={String(card.id)}
                              ref={(el) => {
                                cardRefsRef.current[i] = el;
                              }}
                              className="shrink-0"
                            >
                              <BenefitCard card={card} onOpenVideo={openVideo} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </RevealOnScroll>
                    {!isMobile && cards.length > 2 && (
                      <div
                        className={cn(
                          "hidden min-[1080px]:flex items-center justify-center gap-[16px] w-full",
                          "h-[40px]",
                        )}
                      >
                        {parsedData.left_navigation_icon?.trim() ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={
                              currentIndex === 0 ? undefined : handlePrevClick
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (currentIndex !== 0) handlePrevClick();
                              }
                            }}
                            className={cn(
                              "shrink-0 flex items-center justify-center rounded-full border border-white overflow-hidden",
                              "w-[40px] h-[40px]",
                              currentIndex === 0
                                ? "opacity-50 cursor-not-allowed bg-white/20"
                                : "cursor-pointer bg-white/20 hover:bg-white/30",
                            )}
                            aria-label={
                              parsedData.alt_text_left_nav_icon || "Previous"
                            }
                          >
                            <Image
                              src={parsedData.left_navigation_icon}
                              alt={parsedData.alt_text_left_nav_icon || "Previous"}
                              width={40}
                              height={40}
                              className="w-[40px] h-[40px] object-contain"
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handlePrevClick}
                            disabled={currentIndex === 0}
                            className={cn(
                              "shrink-0 flex items-center justify-center rounded-full border border-white",
                              "w-[40px] h-[40px] p-[8.542px]",
                              currentIndex === 0
                                ? "opacity-50 cursor-not-allowed bg-white/20"
                                : "cursor-pointer bg-white/20 hover:bg-white/30",
                            )}
                            aria-label={
                              parsedData.alt_text_left_nav_icon || "Previous"
                            }
                          >
                            <LeftArrow
                              width={21}
                              height={21}
                              className="text-white [&_path]:stroke-white"
                            />
                          </button>
                        )}
                        <ProgressSlider
                          currentIndex={currentIndex}
                          totalCount={cards.length}
                          stepsCount={Math.max(1, cards.length - 2)}
                          barWidthPx={52}
                          trackClassName="w-[260px] min-[1080px]:w-[247px] h-[6px] bg-white/20"
                          barClassName="bg-white h-[5.125px]"
                        />
                        {parsedData.right_navigation_icon?.trim() ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={
                              currentIndex === cards.length - 1
                                ? undefined
                                : handleNextClick
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (currentIndex !== cards.length - 1)
                                  handleNextClick();
                              }
                            }}
                            className={cn(
                              "shrink-0 flex items-center justify-center rounded-full border border-white overflow-hidden",
                              "w-[40px] h-[40px]",
                              currentIndex === cards.length - 1
                                ? "opacity-50 cursor-not-allowed bg-white/20"
                                : "cursor-pointer bg-white/20 hover:bg-white/30",
                            )}
                            aria-label={
                              parsedData.alt_text_right_nav_icon || "Next"
                            }
                          >
                            <Image
                              src={parsedData.right_navigation_icon}
                              alt={parsedData.alt_text_right_nav_icon || "Next"}
                              width={40}
                              height={40}
                              className="w-[40px] h-[40px] object-contain"
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleNextClick}
                            disabled={currentIndex === cards.length - 1}
                            className={cn(
                              "shrink-0 flex items-center justify-center rounded-full border border-white",
                              "w-[40px] h-[40px] p-[8.542px]",
                              currentIndex === cards.length - 1
                                ? "opacity-50 cursor-not-allowed bg-white/20"
                                : "cursor-pointer bg-white/20 hover:bg-white/30",
                            )}
                            aria-label={
                              parsedData.alt_text_right_nav_icon || "Next"
                            }
                          >
                            <RightArrow
                              width={21}
                              height={21}
                              className="text-white [&_path]:stroke-white"
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>
        <YouTubeVideoOverlay
          isOpen={videoOpen}
          onClose={() => {
            setVideoOpen(false);
            setActiveVideoUrl("");
          }}
          videoUrl={activeVideoUrl}
          title="Program benefit video"
        />
      </>
    );
};